import React, { useState, useEffect, useRef } from "react";
import { StyleSheet } from 'react-native';
import { theme } from "../theme"
import {
  Grommet,
  Page,
  PageContent,
  Text,
  Box,
  Footer,
  Accordion,
  Button
} from "grommet";

import { ACTION_COLOURS } from '../constants';
import { AnimatedBackground } from "../components";
import {
  HeaderSection,
  DropboxSharingSection,
  PricingSection,
  SecuritySection
} from '../sections';
import { useMediaQuery } from 'react-responsive';
import { useAuth } from "../context";

import { getProfile } from '../api'

const Pricing = () => {
  const { isAuthenticated, isLoading, user, login, logout, tokens } = useAuth();

  // Replace error suppression with more targeted approach

  // Only suppress specific warnings if needed
  console.warn = console.error = () => { };

  const isMobile = useMediaQuery({ maxWidth: 768 });

  const [isInternal, setIsInternal] = useState((window.location.hostname === "192.168.20.29") || (window.location.hostname === "100.96.1.18"));
  const [isSharing, setIsSharing] = useState(window.location.href?.includes('sharing') || false);

  // The central source of truth for all security panel data
  // const [inputValues, setInputValues] = useState({});
  const [pricingData, setPricingData] = useState({});

  // Track which deletions are in progress to avoid duplicate operations
  const processingDelete = useRef(new Set());

  // Security panel structure: [{id: 0, label: 'Security 1'}, ...]
  const [panels, setPanels] = useState([]);

  // Which panels are currently expanded in the accordion
  const [activePanel, setActivePanel] = useState([]);

  const [loanSummary, setLoanSummary] = useState({
    totalLoanAmount: '--',
    totalEMV: '--',
    totalLVR: '--'
  });

  const [sharedPanelProps, setSharedPanelProps] = useState({
    loan_term: '',
    guarantor_count: '',
    priority: '',
    borrower_structure:'',
    da_status: ''
  });

  const [nickname, setNickname] = useState('');


  // Function to update shared panel properties
  const updateSharedPanelProps = (newProps) => {
    setSharedPanelProps(prev => ({
      ...prev,
      ...newProps
    }));
  };

  // // DEBUG: Log state changes to help troubleshoot
  // useEffect(() => {
  //   console.log("App.js: securityPanels:", panels);
  //   //console.log("App.js: inputValues:", inputValues);
  //   console.log("App.js: activePanel:", activePanel);
  // }, [panels, activePanel]);

  const addPanel = () => {
    // Generate a unique ID based on the highest existing ID + 1
    const newId = panels.length > 0
      ? Math.max(...panels.map(p => p.id)) + 1
      : 0;

    console.log('App.js: Creating new panel with id:', newId);

    // Add the new panel with both metadata and values in one object
    setPanels(prevPanels => {
      const updatedPanels = [
        ...prevPanels,
        {
          id: newId,
          label: `Security ${prevPanels.length + 1}`, // Based on position, not ID
          values: {} // Initialize empty values object for this panel
        }
      ];
      return updatedPanels;
    });

    // // Automatically open the new panel
    // setActivePanel(prevActivePanel => {
    //   const newPanelIndex = panels.length; // This will be the index of the new panel
    //   return [...prevActivePanel, newPanelIndex];
    // });
    setActivePanel([newId]);
  };

  // After any panel array changes, update all labels to match their position
  useEffect(() => {
    console.log('panels.length', panels.length)

    if (panels.length > 0) {
      // Update panel labels to match their current position
      setPanels(currentPanels =>
        currentPanels.map((panel, index) => ({
          ...panel,
          id: index,
          label: `Security ${index + 1}` // 1-based for user display
        }))
      );
    }
    console.log(panels)
  }, [panels.length]); // Only run when the number of panels changes

  // Improved delete function to handle index updates
  const deletePanel = (id) => {
    console.log(`App.js: Attempting to delete panel with id: ${id}, timestamp: ${Date.now()}`);

    // Check if deletion is already in progress for this ID
    if (processingDelete.current.has(id)) {
      console.log(`App.js: Deletion for id ${id} already in progress, skipping duplicate call.`);
      return;
    }

    // Check if the panel actually exists
    if (!panels.some(panel => panel.id === id)) {
      console.log(`App.js: Panel with id ${id} not found in current panels.`);
      return;
    }

    // Mark this ID as being processed
    processingDelete.current.add(id);

    const confirmed = window.confirm("Are you sure you want to delete this panel?");

    if (confirmed) {
      // Find the panel's position in the array
      const panelIndexToDelete = panels.findIndex(p => p.id === id);
      console.log(`App.js: Deleting panel at index ${panelIndexToDelete} with id ${id}`);

      // Remove the panel from the state
      setPanels(prevPanels => {
        const updatedPanels = prevPanels.filter(panel => panel.id !== id);
        return updatedPanels;
      });

      // Update active panels indices to account for the removed panel
      setActivePanel(prevActiveIndices => {
        // Remove the deleted index from active panels
        const withoutDeletedIndex = prevActiveIndices.filter(idx => idx !== panelIndexToDelete);

        // Adjust indices for panels that come after the deleted one
        return withoutDeletedIndex.map(idx =>
          idx > panelIndexToDelete ? idx - 1 : idx
        );
      });
    }

    // Clear the processing flag after a delay
    setTimeout(() => {
      processingDelete.current.delete(id);
      console.log(`App.js: Cleared processing lock for id ${id}`);
    }, 100);
  };

  // Function to update a specific panel's values
  const updatePanelValues = (id, newValues) => {
    setPanels(prevPanels =>
      prevPanels.map(panel =>
        panel.id === id
          ? { ...panel, values: { ...panel.values, ...newValues } }
          : panel
      )
    );
  };

  // // Effect to ensure the latest panel is active after adding
  // useEffect(() => {
  //   // If there are panels but none are active, activate the first one
  //   if (panels.length > 0 && activePanel.length === 0) {
  //     setActivePanel([0]);
  //   }
  // }, [panels.length, activePanel.length]);
  useEffect(() => {
    if (tokens?.idToken && !nickname) {
      const getNickname = async () => {
        try {
          const res = await getProfile(tokens.idToken);
          setNickname(res?.nickname ?? '');
          console.log('User profile:', res);
        } catch (error) {
          console.error('Failed to get profile:', error);
        }
      }
      getNickname();
    }
  }, [isAuthenticated, tokens, nickname])
  // Calculate loan summary whenever inputValues changes
  useEffect(() => {
    // Skip if no panels exist
    if (panels.length === 0) {
      return;
    }

    //console.log("App.js: Recalculating loan summary from panels:", panels);
    setPricingData({});

    // Calculate totals from all security panels
    const totalLoanAmount = panels
      .map(panel => panel.values?.loan_amount)
      .filter(amount => !isNaN(parseFloat(amount)))
      .reduce((sum, amount) => sum + parseFloat(amount), 0);

    const totalEMV = panels
      .map(panel => panel.values?.market_value)
      .filter(amount => !isNaN(parseFloat(amount)))
      .reduce((sum, amount) => sum + parseFloat(amount), 0);

    let totalLVR = 0;
    if (!isNaN(totalLoanAmount) && !isNaN(totalEMV) && totalEMV > 0) {
      totalLVR = totalLoanAmount / totalEMV;
    }

    setLoanSummary({
      totalEMV: totalEMV,
      totalLoanAmount: totalLoanAmount,
      totalLVR: totalLVR
    });
  }, [JSON.stringify(panels.map(panel => {
    // Create a copy of each panel's values and remove max_lvr
    const panelValues = { ...panel.values };
    if (panelValues) {
      delete panelValues.max_lvr;
    }
    // Return panel with all properties except max_lvr in values
    return {
      ...panel,
      values: panelValues
    };
  }))]);

  // Track previous GNAF values to detect changes
  const prevGnafValues = useRef(new Map());

  // Effect to clear pricing data when GNAF changes
  useEffect(() => {
    let shouldClearPricing = false;
    const panelsToUpdate = [];
    
    // Check for changes without updating references yet
    panels.forEach(panel => {
      const currentGnaf = panel.values?.gnaf;
      const previousGnaf = prevGnafValues.current.get(panel.id);
      
      // If GNAF changed from one value to another (not from undefined to value)
      if (currentGnaf && previousGnaf && currentGnaf !== previousGnaf) {
        shouldClearPricing = true;
        panelsToUpdate.push(panel.id);
      }
    });
    
    // Clear pricing data and max_lvr if any GNAF changed
    if (shouldClearPricing) {
      console.log("GNAF changed, clearing pricing data");
      setPricingData({});
      
      // Clear max_lvr from panels that changed addresses
      panelsToUpdate.forEach(panelId => {
        updatePanelValues(panelId, { max_lvr: undefined });
      });
    }
    
    // Update all references after processing
    panels.forEach(panel => {
      const currentGnaf = panel.values?.gnaf;
      if (currentGnaf) {
        prevGnafValues.current.set(panel.id, currentGnaf);
      }
    });
  }, [JSON.stringify(panels.map(p => ({ id: p.id, gnaf: p.values?.gnaf })))]);

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      padding: 'middle',
    },
  });

  return (
    <Grommet zIndex={1} theme={theme} full style={{ background: 'rgba(0,0,0,0)' }}>
      <Page width={{ min: '365px' }} align='center' flex='grow'>
        <AnimatedBackground />
        <Box id="tracker" style={{ position: 'fixed', left: '-9999px', top: '-9999px' }}></Box>
        <HeaderSection isInternal={isInternal} isSharing={isSharing} />
        {false && (<Text style={{ background: ACTION_COLOURS['REJECT'] }}>Website is under construction. Please come back and check again later.</Text>)}
        {isLoading && (<Text>Loading...</Text>)}
        {isAuthenticated ? (
          <PageContent alignSelf="center" width={{ max: '53rem' }} style={{ padding: '1rem', display: 'flex', flexDirection: 'column' }} zIndex={2}>
            <DropboxSharingSection isInternal={isInternal} isSharing={isSharing} />
            {nickname && (<Text> Hello, {nickname}! </Text>)}
            {/* <div>
              
              <button onClick={() => logout()}>Sign out</button>
            </div> */}
            {panels.length < 1 && !isSharing && (<Text alignSelf="center" >Please add a security to start.</Text>)}
            {/* Security Panels Accordion */}
            <Accordion animate={false} multiple={true} activeIndex={activePanel} onActive={(newActiveIndex) => setActivePanel(newActiveIndex)}>
              {panels.map((panel, index) => (
                <SecuritySection
                  key={panel.id}
                  id={panel.id}
                  index={index}
                  isMobile={isMobile}
                  panel={panel}
                  panels={panels}
                  updatePanel={updatePanelValues}
                  deletePanel={() => deletePanel(panel.id)}

                />
              ))}
            </Accordion>

            {/* Add Security Button */}
            <Box width={'15rem'} alignSelf="center">
              <Button
                alignSelf="center"
                margin={{ top: '1rem' }}
                label='Add Security'
                onClick={addPanel}
                style={{ fontWeight: 'lighter' }}
              />
            </Box>

            {/* Pricing Section (only shown if there are security panels) */}
            {panels.length > 0 && (
              <PricingSection
                isMobile={isMobile}
                pricingData={pricingData}
                setPricingData={setPricingData}
                styles={styles}
                panels={panels}
                updatePanel={updatePanelValues}
                loanSummary={loanSummary}
                sharedPanelProps={sharedPanelProps}
                updateSharedPanelProps={updateSharedPanelProps}
              />
            )}
          </PageContent>
        )
          :
          !isLoading && (
            <PageContent alignSelf="center" width={{ max: '53rem' }} style={{ padding: '1rem', display: 'flex', flexDirection: 'column' }} zIndex={2}>
              <Box width={'15rem'} alignSelf="center">
                <Button
                  alignSelf="center"
                  margin={{ top: '1rem' }}
                  label='Sign in'
                  onClick={() => login()}
                  style={{ fontWeight: 'lighter' }}
                />
              </Box>
            </PageContent>
          )}

        {/* Footer (only shown on mobile if there are security panels) */}
        {isMobile && panels.length > 0 && (
          <Footer pad="large">
            <Text>Powered by SAIL&trade;</Text>
          </Footer>
        )}
      </Page>
    </Grommet>
  );
};

export { Pricing };